import ajax from './ajax';
import { AppModule } from '@/store/modules/app';
import { subscribeToAgenticTaskLog, AgenticTaskLogCallbacks } from './agenticTaskLogFirestore';
import { Unsubscribe } from 'firebase/firestore';

// Progress step messages mapping (for display purposes)
export const CV_PROGRESS_STEPS: Record<number, string> = {
  1: 'Converting document...',
  2: 'Loading agency data...',
  3: 'Analyzing CV...',
  4: 'Processing results...',
  5: 'Geocoding address...',
  6: 'Finalizing...',
};

/**
 * Response from the CV parse start endpoint
 */
export interface CVParseStartResponse {
  taskId: string;
}

/**
 * Callbacks for CV parsing progress
 */
export interface CVParserCallbacks {
  onProgress: (step: number, totalSteps: number, message: string) => void;
  onThinking: (thinking: string) => void;
  onResult: (result: any) => void;
  onError: (error: string) => void;
  onClose?: () => void;
}

/**
 * Start CV parsing with Firestore streaming for real-time progress and thinking updates.
 *
 * This function:
 * 1. Uploads the file to the backend, which creates the AgenticTask in Postgres
 *    and AgenticTaskLog in Firestore, then starts processing asynchronously
 * 2. Subscribes to the Firestore document for real-time progress updates
 * 3. Returns an unsubscribe function to stop listening
 *
 * @param file - The CV file to parse
 * @param payRate - Pay rate to apply to matched positions
 * @param summaryFormat - Format for professional summary ('bullets' or 'paragraphs')
 * @param callbacks - Callbacks for progress, thinking, result, and error events
 * @returns Promise that resolves to an unsubscribe function
 */
export async function startCVParseWithFirestore(
  file: File,
  payRate: number,
  summaryFormat: string,
  callbacks: CVParserCallbacks
): Promise<Unsubscribe> {
  // Get agency ID for Firestore path
  const agencyId = AppModule.currentAgencyContext?.id;
  if (!agencyId) {
    callbacks.onError('Agency context required');
    callbacks.onClose?.();
    return () => {}; // Return no-op unsubscribe
  }

  // Phase 1: Upload file and get task ID
  const formData = new FormData();
  formData.append('file', file);
  formData.append('payRate', payRate.toString());
  formData.append('summaryFormat', summaryFormat);

  let taskId: string;
  try {
    const response = await ajax.post<CVParseStartResponse>(
      '/api/v1/agency/workers/import-cv/start',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    taskId = response.data.taskId;
    console.log('[CV Parser Firestore] Task started with ID:', taskId);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || 'Failed to start CV parsing';
    callbacks.onError(message);
    callbacks.onClose?.();
    return () => {}; // Return no-op unsubscribe
  }

  // Phase 2: Subscribe to Firestore for real-time updates
  const unsubscribe = subscribeToAgenticTaskLog(agencyId, taskId, callbacks);

  return unsubscribe;
}

/**
 * Start CV parsing with streaming (wrapper for backward compatibility)
 *
 * This is a Promise-based wrapper that resolves when the task completes.
 * Use startCVParseWithFirestore if you need access to the unsubscribe function.
 *
 * @param file - The CV file to parse
 * @param payRate - Pay rate to apply to matched positions
 * @param summaryFormat - Format for professional summary ('bullets' or 'paragraphs')
 * @param callbacks - Callbacks for progress, thinking, result, and error events
 * @returns Promise that resolves when the task completes (result or error)
 */
export function startCVParseWithStreaming(
  file: File,
  payRate: number,
  summaryFormat: string,
  callbacks: CVParserCallbacks
): Promise<void> {
  return new Promise(async (resolve) => {
    let unsubscribe: Unsubscribe | null = null;

    const wrappedCallbacks: CVParserCallbacks = {
      onProgress: callbacks.onProgress,
      onThinking: callbacks.onThinking,
      onResult: (result) => {
        callbacks.onResult(result);
      },
      onError: (error) => {
        callbacks.onError(error);
      },
      onClose: () => {
        callbacks.onClose?.();
        // Clean up subscription when done
        if (unsubscribe) {
          unsubscribe();
        }
        resolve();
      }
    };

    unsubscribe = await startCVParseWithFirestore(file, payRate, summaryFormat, wrappedCallbacks);
  });
}

export default {
  startCVParseWithStreaming,
  startCVParseWithFirestore,
  CV_PROGRESS_STEPS,
};
